import {
  APPROVED_INTERNAL_MCP_SERVERS,
  HARDENING_AGENT_NAMES,
  OBSERVED_NETWORK_DESTINATIONS
} from "./getDemoData"
import { toOptionValues, uniqueStrings } from "./optionValues"

import type { PolicyRecord } from "./getDemoData.types"
import type {
  HardeningAgent,
  HardeningInput,
  HardeningSuggestion,
  HardeningSuggestionId,
  ObservedAttributes
} from "./getHardeningSuggestions.types"
import type { PolicyRecordInput } from "./useAiPolicies.types"

const INTENDED_TOOL_BEHAVIOR = ["within-intended-behavior"]
const FALLBACK_MODEL_PROVIDERS = ["openai", "anthropic"]
const FALLBACK_TOOL_CATEGORIES = [
  "code-development",
  "communication",
  "knowledge-memory"
]

const getToolGroups = (agent: HardeningAgent) => [
  ...(agent.dataSources ?? []),
  ...(agent.dataSinks ?? [])
]

const withFallback = (values: string[], fallback: string[]) =>
  values.length > 0 ? values : fallback

// What the agent's own tools and models look like today, as rule values.
export const getObservedAttributes = (
  agent: HardeningAgent
): ObservedAttributes => {
  const groups = getToolGroups(agent)
  const tools = groups.flatMap((group) => group.tools ?? [])

  return {
    modelProvider: toOptionValues(
      "modelProvider",
      uniqueStrings((agent.models ?? []).map((model) => model.vendor))
    ),
    // Builds on the environment policy: only MCP servers it already approves.
    mcpServer: uniqueStrings(tools.map((tool) => tool.mcpClient)).filter(
      (server) => APPROVED_INTERNAL_MCP_SERVERS.includes(server)
    ),
    toolCategory: toOptionValues(
      "toolCategory",
      groups.map((group) => group.category)
    )
  }
}

const allow = (
  id: HardeningSuggestionId,
  value: string[]
): HardeningSuggestion => ({
  id,
  side: "allow",
  condition: { id, value },
  checked: true
})

export const isHardeningCandidate = (agentName: string) =>
  HARDENING_AGENT_NAMES.includes(agentName)

// The five rule types the hardening modal suggests, in modal order.
export const HARDENING_RULE_IDS: HardeningSuggestionId[] = [
  "modelProvider",
  "mcpServer",
  "networkDestination",
  "toolCategory",
  "toolBehavior"
]

// The suggested rule types the agent's own policy does not hold yet. Rules of an
// inherited policy don't count: the agent's own policy replaces it.
export const getMissingHardeningRules = (
  ownPolicy: PolicyRecordInput | undefined
): HardeningSuggestionId[] => {
  const held = new Set(
    (ownPolicy?.allowed ?? []).flatMap((group: PolicyRecord) =>
      group.rules.map((rule) => rule.id)
    )
  )

  return HARDENING_RULE_IDS.filter((id) => !held.has(id))
}

// Whether the agent can still be hardened: the agents table badge and the
// drawer banner both show exactly while this holds.
export const hasHardeningSuggestions = (input: {
  agentName: string
  ownPolicy?: PolicyRecordInput
}) =>
  isHardeningCandidate(input.agentName) &&
  getMissingHardeningRules(input.ownPolicy).length > 0

// The allow rules that lock the agent to what code and runtime analysis
// observed: model providers, MCP servers, network destinations, tool
// categories, and each tool's intended behavior. Only the rule types its own
// policy does not hold yet are suggested.
export const getHardeningSuggestions = (
  input: HardeningInput
): HardeningSuggestion[] => {
  const { agent, ownPolicy } = input

  if (!isHardeningCandidate(agent.name)) return []

  const observed = getObservedAttributes(agent)
  const suggestions = [
    allow(
      "modelProvider",
      withFallback(observed.modelProvider, FALLBACK_MODEL_PROVIDERS)
    ),
    allow(
      "mcpServer",
      withFallback(observed.mcpServer, APPROVED_INTERNAL_MCP_SERVERS)
    ),
    allow("networkDestination", OBSERVED_NETWORK_DESTINATIONS),
    allow(
      "toolCategory",
      withFallback(observed.toolCategory, FALLBACK_TOOL_CATEGORIES)
    ),
    allow("toolBehavior", INTENDED_TOOL_BEHAVIOR)
  ]
  const missing = getMissingHardeningRules(ownPolicy)

  return suggestions.filter((suggestion) => missing.includes(suggestion.id))
}
